import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataCoreService, DlistDto, DlistFieldDto, environment, FormDto, GetDlist_Request, GetDlist_Response, GetFormMetadata_Request, GetFormMetadata_Response } from '@dabnet-kamu/libs/ui';
import { BehaviorSubject, catchError, finalize, Observable, tap, throwError } from 'rxjs';

/** DKM-5023: Ana ekrandaki "İzin Bilgileri" satırının özeti. */
export interface PersonelIzinOzeti {
  hakEdilen: number;
  kullanilan: number;
  kalan: number;
  /** Hangi yılların toplandığı, ör. "2025 + 2026 yılları" / "tüm yıllar". */
  kapsam: string;
}

@Injectable({
  providedIn: 'root'
})
export class OpeningScreenService {

  private _formMetaData: BehaviorSubject<FormDto | null> = new BehaviorSubject(null);
  formMetaData$: Observable<FormDto> = this._formMetaData.asObservable();
 
   //vBimPersonelTelefonDetay
   private _gridCtlvBimPersonelTelefonDetay: BehaviorSubject<DlistDto | null> =
     new BehaviorSubject(null);
   public gridCtlvBimPersonelTelefonDetay$ = this._gridCtlvBimPersonelTelefonDetay.asObservable();
 
   private _fieldsvBimPersonelTelefonDetay: BehaviorSubject<DlistFieldDto[] | null> =
     new BehaviorSubject(null);
   public fieldsvBimPersonelTelefonDetay$ = this._fieldsvBimPersonelTelefonDetay.asObservable();

   errorResponse: any = {};
 
   constructor(
     private dataCoreService: DataCoreService,
     private _httpClient: HttpClient
   ) {}
 
   getvBimPersonelTelefonDetayData() {
     const dataSource = this.dataCoreService.getOData('vBimPersonelTelefonDetay');
     dataSource.pageSize(9999999)
     return dataSource;
   }

   /**
    * DKM-5023: Personelin yıllık izin özeti (ana ekran).
    *
    * Kural, İzin Bilgileri Girişi panelindeki kalan izinle aynı
    * (libs/ika ... ika-personel-izin-panel.component.ts > kalanIzinYukle):
    *   Memur (IzinTuru = 'M') : içinde bulunulan yıl + bir önceki yıl
    *   Diğerleri              : tüm yıllar
    *
    * Kaynak: vIkaPersonelIzinHakedis (VWD_IKA_PERSONEL_IZIN_HAKEDIS), yıl başına bir satır.
    *   KullanilanYillikIzinSure : o yıl kullanılan yıllık izin (iptal edilenler hariç)
    *   KalanYillikIzin          : HakedisSure + EkIzinSure - kullanılan (view hesaplıyor)
    * Hak edilen = kalan + kullanılan olarak türetiliyor ki üç sayı ekranda her zaman tutarlı olsun.
    *
    * Sayfalama kapalı: getOData varsayılanı 10 satırlık sayfa; 10 yıldan uzun kıdemi
    * olan personelde toplam eksik çıkardı.
    */
   async getIzinOzeti(personelKod: number): Promise<PersonelIzinOzeti | null> {
     if (!personelKod) return null;

     // DKM-5233 Clara: eski hâli
     // const dataSource = this.dataCoreService.getOData(
     //   'vIkaPersonelIzinHakedis', null, undefined, 'Kod', undefined, false
     // );
     // Kendi=E: servis yalnız oturumdaki personelin hakedişlerini döndürür (vIkaPersonelIzinHakedisBs).
     const dataSource = this.dataCoreService.getOData(
       'vIkaPersonelIzinHakedis?Kendi=E', null, undefined, 'Kod', undefined, false
     );
     dataSource.filter(['PersonelKod', '=', personelKod]);
     const satirlar: any[] = (await dataSource.load()) || [];

     if (!satirlar.length) {
       return { hakEdilen: 0, kullanilan: 0, kalan: 0, kapsam: 'hakediş kaydı yok' };
     }

     const memur = satirlar[0]?.IzinTuru === 'M';
     const buYil = new Date().getFullYear();
     const kapsam = memur
       ? satirlar.filter((x) => x.Yil === buYil || x.Yil === buYil - 1)
       : satirlar;

     const kullanilan = kapsam.reduce((t, x) => t + (Number(x.KullanilanYillikIzinSure) || 0), 0);
     const kalan = kapsam.reduce((t, x) => t + (Number(x.KalanYillikIzin) || 0), 0);

     return {
       hakEdilen: kalan + kullanilan,
       kullanilan,
       kalan,
       kapsam: memur ? `${buYil - 1} + ${buYil} yılları` : 'tüm yıllar',
     };
   }

   getDlist(formName: string): any {
     const request = new GetDlist_Request({
       dListCode: formName,
     });
     this._httpClient
       .post<GetDlist_Response>(
         `${environment.ortakService}/System/GetDList`,
         request
       )
       .pipe(
         tap((response) => {
           if (formName === 'vBimPersonelTelefonDetay') {
             this._gridCtlvBimPersonelTelefonDetay.next(response.dListMetadata);
             this._fieldsvBimPersonelTelefonDetay.next(response.dListMetadata.fields);
             
             // Fetch form metadata
             this.getFormMetadata(formName);
           }
         }),
         catchError((err) => throwError(err)),
         finalize(() => {})
       )
       .subscribe();
   }

   private getFormMetadata(formName: string): void {
     const request = new GetFormMetadata_Request({
       formAd: 'GenFormForm',
       altFormAd: '',
       kod: 0,
       logYn: false
     });
     
     this._httpClient
       .post<GetFormMetadata_Response>(
         `${environment.ortakService}/System/GetFormMetadata`,
         request
       )
       .pipe(
         tap((response) => {
           if (response?.formMetadata) {
             this._formMetaData.next(response.formMetadata);
           }
         }),
         catchError((err) => {
           console.error('Error fetching form metadata:', err);
           return throwError(err);
         })
       )
       .subscribe();
   }
}
